import { Thread as UnifiedThread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { isSmsMessageItem, isCallRecordItem, isAiActivityItem } from '../../../utils/thread-items-utils';
import { ListUnifiedThreadsIO } from '../../types';
import { CachedEndpointData, EndpointQueryData, ResourceUpdaterFnArgs, ResourceUpdaterFnResult } from '../types';
import { getListUnifiedThreadsResourceIndexes, threadCouldBeInListUnifiedThreadsResponse } from '../utils';
import { combinedUnifiedThreadMatchFn } from '../utils/combined-resource-match-functions';

type UpdateType = 'filter' | 'update' | 'update-in-place' | 'insert' | 'none';

// Helper function to get the createdAt timestamp from the first ThreadItem
const getThreadItemCreatedAt = (thread?: UnifiedThread): string | undefined => {
  if (!thread?.items?.length) return undefined;

  const firstItem = thread.items[0];

  if (!firstItem) return undefined;

  if (isSmsMessageItem(firstItem)) {
    return firstItem.smsMessage?.createdAt;
  }
  if (isCallRecordItem(firstItem)) {
    return firstItem.callRecord?.startedAt; // CallRecord uses startedAt, not createdAt
  }
  if (isAiActivityItem(firstItem)) {
    return firstItem.aiActivity?.createdAt;
  }

  return undefined;
};

// Helper function to get the createdAt timestamp from the newest SMS item only
// Used to determine if thread should move to top based on SMS activity only
const getNewestSmsCreatedAt = (thread?: UnifiedThread, excludeAutomated = false): string | undefined => {
  if (!thread?.items?.length) return undefined;

  // Find the newest SMS item in the thread
  let newestSmsItem;

  if (excludeAutomated) {
    // Find the first (newest) manual SMS item (not automated)
    newestSmsItem = thread.items.find((item) => isSmsMessageItem(item) && !item.smsMessage?.autogeneratedBy);
  } else {
    // Find the first (newest) SMS item regardless of automation
    newestSmsItem = thread.items.find(isSmsMessageItem);
  }

  // Type guard to ensure we have an SMS item
  if (newestSmsItem && isSmsMessageItem(newestSmsItem)) {
    return newestSmsItem.smsMessage?.createdAt;
  }

  return undefined;
};

export const getListUnifiedThreadsUpdateType = ({
  matchValues,
  matchFn,
  matchStrategy,
  queryData,
  originalResource,
  updatedResource,
}: ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>): UpdateType => {
  const request = queryData[0][2];
  if (!request || !request.locationIds?.length) {
    return 'none';
  }
  const typedRequest = request as Compulsory<ListUnifiedThreadsIO['input'], 'locationIds'>;

  const originalIsInQuery = originalResource
    ? queryData[1].pages.some((page) =>
        page.threads.some((thread) => combinedUnifiedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread))
      )
    : false;

  const updatedShouldBeInQuery = threadCouldBeInListUnifiedThreadsResponse({
    thread: updatedResource,
    listUnifiedThreadsRequest: typedRequest,
  });

  // Only move thread to top if SMS activity has changed, not for call records or other items
  // Consider excludeAutomatedLastMessage setting for proper thread ordering
  const excludeAutomated = !!typedRequest.unifiedFilter?.excludeAutomatedLastMessage;
  const originalSmsCreatedAt = getNewestSmsCreatedAt(originalResource, excludeAutomated);
  const updatedSmsCreatedAt = getNewestSmsCreatedAt(updatedResource, excludeAutomated);

  // Only move thread based on SMS activity, not call records or other items
  const createdAtHasChanged = originalSmsCreatedAt !== updatedSmsCreatedAt;

  if (originalIsInQuery && updatedShouldBeInQuery) {
    return createdAtHasChanged ? 'update' : 'update-in-place';
  } else if (originalIsInQuery && !updatedShouldBeInQuery) {
    return 'filter';
  } else if (!originalIsInQuery && updatedShouldBeInQuery) {
    return 'insert';
  } else {
    return 'none';
  }
};

export const filterListUnifiedThreadsData = ({
  matchValues,
  matchFn,
  matchStrategy,
  queryData,
}: Pick<
  ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>,
  'matchValues' | 'matchFn' | 'matchStrategy' | 'queryData'
>): CachedEndpointData<'ListUnifiedThreads'> => {
  const data = queryData[1];
  if (!data) {
    return queryData[1];
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      threads: page.threads.filter(
        (thread) => !combinedUnifiedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread)
      ),
    })),
  };
};

export const updateListUnifiedThreadsData = ({
  matchValues,
  matchFn,
  matchStrategy,
  updatedResource,
  queryData,
  inPlace,
}: Pick<
  ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>,
  'matchValues' | 'matchFn' | 'matchStrategy' | 'updatedResource' | 'queryData'
> & { inPlace: boolean }): CachedEndpointData<'ListUnifiedThreads'> | undefined => {
  const data = queryData[1];
  if (!data) {
    return queryData[1];
  }

  if (inPlace) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        threads: page.threads.map((thread) =>
          combinedUnifiedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread) ? updatedResource : thread
        ),
      })),
    };
  }

  const filteredData = filterListUnifiedThreadsData({
    queryData,
    matchValues,
    matchFn,
    matchStrategy,
  });

  return insertListUnifiedThreadsData({
    queryData: [queryData[0], filteredData],
    updatedResource,
  });
};

export const insertListUnifiedThreadsData = ({
  queryData,
  updatedResource,
}: Pick<ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>, 'queryData' | 'updatedResource'>):
  | CachedEndpointData<'ListUnifiedThreads'>
  | undefined => {
  const data = queryData[1] ?? {
    pages: [],
    pageParams: [],
  };

  const indexesFromNextOlderThread = getListUnifiedThreadsResourceIndexes({
    data,
    matchValues: { threadId: '', locationId: '' }, // Only use matchFn
    matchFn: (thread) => {
      const threadCreatedAt = getThreadItemCreatedAt(thread);
      const newCreatedAt = getThreadItemCreatedAt(updatedResource);
      if (threadCreatedAt === undefined || newCreatedAt === undefined) {
        return false;
      }

      return threadCreatedAt < newCreatedAt;
    },
    matchStrategy: 'or',
  });

  const hasOlderThread =
    indexesFromNextOlderThread.pageIndex !== undefined && indexesFromNextOlderThread.pageIndex !== -1;
  const queryHasLastPage = data.pages.some((page) => !page.olderPageToken);

  const canInsert = hasOlderThread || queryHasLastPage;
  if (!canInsert) {
    return undefined;
  }

  // If the query has the last page, but we don't have an older thread, we should insert at the end of the last page
  if (queryHasLastPage && !hasOlderThread) {
    indexesFromNextOlderThread.pageIndex = Math.min(data.pages.length - 1, 0);
    indexesFromNextOlderThread.threadIndex = Math.min(data.pages[data.pages.length - 1]?.threads.length ?? 0, 0);
  }

  return {
    ...data,
    pages: data.pages.map((page, pageIndex) => {
      if (pageIndex === indexesFromNextOlderThread.pageIndex) {
        const newerThreads = page.threads.slice(0, indexesFromNextOlderThread.threadIndex);
        const olderThreads = page.threads.slice(indexesFromNextOlderThread.threadIndex);
        return {
          ...page,
          threads: [...newerThreads, updatedResource, ...olderThreads],
        };
      }

      return page;
    }),
  };
};

export const listUnifiedThreadsThreadUpdater = (
  args: ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>,
  providedUpdateType?: UpdateType
): ResourceUpdaterFnResult<'ListUnifiedThreads'> => {
  const updateType = providedUpdateType ?? getListUnifiedThreadsUpdateType(args);

  const { matchValues, matchFn, matchStrategy, queryData, updatedResource } = args;

  switch (updateType) {
    case 'filter':
      return filterListUnifiedThreadsData({
        queryData,
        matchValues,
        matchFn,
        matchStrategy,
      });
    case 'insert':
      return insertListUnifiedThreadsData({ queryData, updatedResource });
    case 'update':
    case 'update-in-place':
      return updateListUnifiedThreadsData({
        matchValues,
        matchFn,
        matchStrategy,
        updatedResource,
        queryData,
        inPlace: updateType === 'update-in-place',
      });
    case 'none':
      return undefined;
  }
};

// We should always filter last to avoid cache misses after filtering
const UPDATE_TYPE_ORDER: UpdateType[] = ['insert', 'update-in-place', 'update', 'filter', 'none'] as const;

export const orderListUnifiedThreadsThreadUpdatesByUpdateType = (
  queries: EndpointQueryData<'ListUnifiedThreads'>[],
  updaterArgs: Omit<ResourceUpdaterFnArgs<'ListUnifiedThreads', UnifiedThread>, 'queryData'>
): { queryData: EndpointQueryData<'ListUnifiedThreads'>; updateType: UpdateType }[] => {
  const queriesWithUpdateType = queries.map((queryData) => {
    let { updatedResource } = updaterArgs;
    const filteredTagIds = queryData[0][2]?.unifiedFilter?.inboxTags || [];
    if (filteredTagIds.length) {
      // For unified threads, uniqueTags are InboxTags with entityId instead of smsId
      updatedResource = {
        ...updatedResource,
        uniqueTags: updatedResource.uniqueTags?.filter((tag) => filteredTagIds.includes(tag.tagId)),
      };
    }
    const updateType = getListUnifiedThreadsUpdateType({
      ...updaterArgs,
      updatedResource,
      queryData,
    });

    return { queryData, updateType };
  });

  return queriesWithUpdateType.sort((a, b) => {
    const aIndex = UPDATE_TYPE_ORDER.indexOf(a.updateType);
    const bIndex = UPDATE_TYPE_ORDER.indexOf(b.updateType);
    return aIndex - bIndex;
  });
};
